<template>
  <div>
    <v-btn
      v-if="props.buttonType === 'text'"
      v-show="!props.hideButton"
      text
      size="small"
      color="primary"
      :class="props.classProp"
      :disabled="props.isDisabled"
      @click.stop="showDialog = true"
    >
      {{ props.buttonName }}
    </v-btn>
    <v-btn
      v-else-if="props.buttonType === 'outlined'"
      v-show="!props.hideButton"
      variant="outlined"
      size="small"
      :class="props.classProp"
      :disabled="props.isDisabled"
      @click.stop="showDialog = true"
    >
      {{ props.buttonName }}
    </v-btn>
    <v-btn
      v-else
      v-show="!props.hideButton"
      size="small"
      color="primary"
      :class="props.classProp"
      :disabled="props.isDisabled"
      @click.stop="showDialog = true"
    >
      {{ props.buttonName }}
    </v-btn>

    <v-dialog v-model="showDialog" :max-width="props.dialogWidth" persistent no-click-animation>
      <v-card>
        <v-card-title class="d-flex mt-2 ml-2">
          <v-icon v-if="props.showAlert" color="red" medium class="mr-2">mdi-alert-circle</v-icon>
          <span class="">{{ props.title }}</span>
        </v-card-title>

        <v-card-text v-if="props.content">
          <div class="d-flex align-start">
            <v-icon v-if="props.showAlertInTextArea" color="orange" medium class="mr-2">mdi-alert-circle</v-icon>
            <div>
              <div v-for="(text, index) in props.content.split('\n')" :key="index">
                {{ text ? text : '&nbsp;' }}
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-text v-if="props.contentList">
          <div class="d-flex align-start">
            <v-icon v-if="props.showAlertInTextArea" color="orange" medium class="mr-2">mdi-alert-circle</v-icon>
            <div>
              <div v-for="(text, index) in props.contentList" :key="index">
                <div
                  v-if="typeof text === 'object'"
                  :style="text.style !== undefined ? text.style : ''"
                  :class="text.class !== undefined ? text.class : ''"
                >
                  {{ text.message ? text.message : '&nbsp;' }}
                </div>
                <div v-else>
                  <div v-for="(_text, _index) in text.split('\n')" :key="_index">
                    {{ _text ? _text : '&nbsp;' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-text class="py-0">
          <div class="overflow-auto">
            <slot name="customContent"></slot>
          </div>
        </v-card-text>

        <v-card-actions class="pa-5">
          <v-btn
            v-for="(text, index) in props.specialButton"
            :key="index"
            color="red"
            variant="outlined"
            @click="special(text)"
          >
            {{ text }}
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn v-if="props.showCancel" color="primary" variant="outlined" @click="cancel"> Cancel </v-btn>

          <v-btn v-if="props.useYesNo" :color="noColor" :variant="noVariant" @click="no">
            {{ props.noText }}
          </v-btn>

          <v-btn v-if="props.useYesNo" :color="yesColor" variant="flat" @click="yes">
            {{ props.yesText }}
          </v-btn>

          <v-btn
            v-if="!props.useYesNo"
            :color="yesColor"
            :variant="yesColor == 'primary' ? 'text' : 'outlined'"
            @click="ok"
          >
            {{ props.yesText == 'Yes' ? props.okButtonName : props.yesText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
// import, props, emits, use
const props = defineProps({
  title: {
    type: String,
    required: true,
    default: 'Confirm'
  },
  content: {
    type: String,
    default: ''
  },
  contentList: {
    type: Array,
    default: undefined
  },
  showAlert: {
    type: Boolean,
    default: false
  },
  showAlertInTextArea: {
    type: Boolean,
    default: false
  },
  buttonName: {
    type: String,
    default: 'Confirm'
  },
  buttonType: {
    type: String,
    default: ''
  },
  dialogWidth: {
    type: [String, Number],
    default: '450'
  },
  isDisabled: {
    type: Boolean,
    default: false
  },
  hideButton: {
    type: Boolean,
    default: false
  },
  classProp: {
    type: String,
    default: undefined
  },
  // useYesNo: true, showCancel: false    - Yes/No
  // useYesNo: true, showCancel: true     - Yes/No/Cancel
  // useYesNo: false, showCancel: true    - Cancel/OK (default)
  // useYesNo: false, showCancel: false   - OK
  useYesNo: {
    type: Boolean,
    default: false
  },
  showCancel: {
    type: Boolean,
    default: true
  },
  okButtonName: {
    type: String,
    default: 'OK'
  },
  yesText: {
    type: String,
    default: 'Yes'
  },
  noText: {
    type: String,
    default: 'No'
  },
  yesColor: {
    type: String,
    default: 'primary'
  },
  noColor: {
    type: String,
    default: 'primary'
  },
  noVariant: {
    type: String,
    default: 'outlined'
  },
  specialButton: {
    type: Array,
    required: false,
    default() {
      return []
    }
  }
})
const emit = defineEmits(['confirm:cancel', 'confirm:ok', 'confirm:yes', 'confirm:no', 'confirm:special'])

// data
const showDialog = ref(false)
const resolve = ref(null)
const reject = ref(null)

// watch
watch(showDialog, (newVal) => {
  console.log(`ConfirmDialog > showDialog: ${newVal}`)
  if (newVal) {
    document.addEventListener('keypress', handleEnter)
  } else {
    document.removeEventListener('keypress', handleEnter)
  }
})

// method
const confirm = async () => {
  showDialog.value = true
  return new Promise((_resolve, _reject) => {
    resolve.value = _resolve
    reject.value = _reject
  })
}

const cancel = () => {
  if (resolve.value) {
    resolve.value('cancel')
  }
  emit('confirm:cancel')
  showDialog.value = false
}

const ok = () => {
  if (resolve.value) {
    resolve.value('ok')
  }
  emit('confirm:ok')
  showDialog.value = false
}

const yes = () => {
  if (resolve.value) {
    resolve.value('yes')
  }
  emit('confirm:yes')
  showDialog.value = false
}

const no = () => {
  if (resolve.value) {
    resolve.value('no')
  }
  emit('confirm:no')
  showDialog.value = false
}

const special = (text) => {
  if (resolve.value) {
    resolve.value('special')
  }
  emit('confirm:special', text)
  showDialog.value = false
}

const handleEnter = (e) => {
  if (!showDialog.value) {
    document.removeEventListener('keypress', handleEnter)
    return
  }

  if (e.key === 'Enter') {
    if (props.useYesNo === false && props.showCancel === false) {
      // OK
      ok()
    }
  }
}

// expose
defineExpose({
  confirm
})
</script>

<style scoped></style>
